import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import {
  Users,
  ShieldCheck,
  Wrench,
  Truck,
  Heart,
  FileText,
  Users2,
  Building2,
  Briefcase,
} from "lucide-react";
import heroImg from "@/assets/hero-drilling.jpg";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";

function Reveal({
  children,
  delay = 0,
  direction = "up",
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  direction?: "up" | "left" | "right";
  className?: string;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-8%" });
  const hidden =
    direction === "up"
      ? { opacity: 0, y: 40 }
      : direction === "left"
        ? { opacity: 0, x: 50 }
        : { opacity: 0, x: -50 };
  const visible = direction === "up" ? { opacity: 1, y: 0 } : { opacity: 1, x: 0 };
  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={{
        hidden,
        visible: { ...visible, transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] } },
      }}
    >
      {children}
    </motion.div>
  );
}

function StaggerGrid({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-8%" });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: 0.15, delayChildren: 0.1 } },
      }}
    >
      {children}
    </motion.div>
  );
}

function StaggerItem({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <motion.div
      className={className}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
      }}
    >
      {children}
    </motion.div>
  );
}

const iconMap: Record<string, any> = {
  Users, ShieldCheck, Wrench, Truck, Heart, FileText, Users2, Building2, Briefcase
};

export default function Management() {
  const { t } = useTranslation("management");
  const rawTeam = t("team", { returnObjects: true }) as { icon: string; title: string; description: string }[];
  const managementTeam = rawTeam.map(member => ({
    ...member,
    icon: iconMap[member.icon] || Users
  }));

  return (
    <>
      <Helmet>
        <title>{t("meta_title")}</title>
        <meta name="description" content={t("meta_desc")} />
        <meta name="keywords" content={t("meta_keywords")} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={t("meta_title")} />
        <meta property="og:description" content={t("meta_desc")} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://salmamydrill.com/management" />
        <meta property="og:image" content={heroImg} />
        <meta property="og:locale" content="fr_FR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t("meta_title")} />
        <meta name="twitter:description" content={t("meta_desc")} />
        <meta name="twitter:image" content={heroImg} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Archivo:wght@600;700;800;900&family=Inter:wght@400;500;600&display=swap" />
      </Helmet>
      <div className="min-h-screen bg-background text-foreground flex flex-col overflow-x-hidden">
        <Nav />

        {/* Dark Premium Hero */}
        <section className="relative bg-surface text-surface-foreground pt-32 pb-24 md:pt-48 md:pb-32 px-6 grain overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-surface via-surface to-background/5" />
          <motion.div
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 0.3, scale: 1 }}
            transition={{ duration: 2.2, ease: "easeOut" }}
            className="absolute right-0 top-0 w-1/2 h-full pointer-events-none mix-blend-screen"
          >
            <img
              src={heroImg}
              alt="Management Team"
              className="w-full h-full object-cover mask-image-l"
            />
          </motion.div>

          <div className="relative z-10 mx-auto max-w-7xl flex flex-col items-start">
            <motion.span
              className="eyebrow inline-flex items-center gap-3 text-primary tracking-[0.3em] font-bold text-xs uppercase mb-8"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
            >
              <span className="w-12 h-px bg-primary/40 block"></span>
              {t("hero.eyebrow")}
            </motion.span>
            <div className="overflow-hidden mb-4">
              <motion.div
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 1.0, ease: [0.16, 1, 0.3, 1] }}
              >
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-display font-black leading-[1.1] tracking-tighter uppercase text-surface-foreground">
                  {t("hero.h1_line1")}
                  <br />
                  <span className="text-primary italic">{t("hero.h1_accent")}</span>
                </h1>
              </motion.div>
            </div>
            <motion.p
              className="text-base sm:text-xl text-surface-foreground/70 max-w-2xl leading-relaxed border-l-2 border-primary pl-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              {t("hero.subtitle")}
            </motion.p>
          </div>
        </section>

        {/* Main Content */}
        <main className="flex-1 pb-32">
          <div className="mx-auto max-w-7xl px-6 mt-[-3rem] relative z-20">
            <Reveal direction="left">
              <div className="bg-white border border-border p-6 sm:p-8 md:p-14 shadow-2xl">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-black leading-[1.1] tracking-tight mb-6 sm:mb-10 flex flex-col gap-2">
                  {t("main.title_line1")}
                  <span className="text-foreground/30 italic">{t("main.title_accent")}</span>
                </h2>
                <p className="text-base sm:text-lg leading-relaxed text-foreground/70 mb-8 sm:mb-12">
                  {t("main.desc")}
                </p>

                <hr className="border-border my-12" />

                <h3 className="font-display text-xl sm:text-2xl font-black uppercase mb-6 sm:mb-8 flex items-center gap-3">
                  <span className="w-8 h-1 bg-primary inline-block"></span> {t("main.section_title")}
                </h3>

                <StaggerGrid className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {managementTeam.map((member, index) => (
                    <StaggerItem key={member.title}>
                      <div className="group relative overflow-hidden bg-surface-foreground/5 border-l-4 border-surface p-6 transition-all hover:bg-surface hover:border-primary">
                        <div className="relative z-10 flex items-start gap-4">
                          <div className="w-12 h-12 bg-white flex items-center justify-center shrink-0 group-hover:bg-primary transition-colors">
                            <member.icon
                              className="w-6 h-6 text-surface group-hover:text-primary-foreground"
                              strokeWidth={1.5}
                            />
                          </div>
                          <div>
                            <h4 className="text-lg sm:text-xl font-bold uppercase tracking-wide text-foreground group-hover:text-surface-foreground transition-colors mb-2">
                              {member.title}
                            </h4>
                            <p className="text-xs sm:text-sm text-foreground/60 group-hover:text-surface-foreground/70 leading-relaxed">
                              {member.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </StaggerItem>
                  ))}
                </StaggerGrid>
              </div>
            </Reveal>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}
